import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Save, ToggleRight, Settings, Globe, Info, User, KeyRound, Mail } from "lucide-react";

export default function AdminSettings() {
  const { t } = useI18n();
  const { user } = useAuth();

  const SETTING_LABELS = {
    default_language: { label: t("settings.name_label") === "settings.name_label" ? "Default Language" : t("settings.language"), desc: t("settings.change_password_desc") === "settings.change_password_desc" ? "" : "", type: "language" },
    available_languages: { label: t("admin.available") + " " + t("admin.language"), type: "text" },
    vat_rate: { label: t("admin.vat_pct"), type: "number", suffix: "%" },
    company_name: { label: t("admin.company_name"), type: "text" },
    company_address: { label: t("admin.address"), type: "text" },
    company_vat: { label: t("admin.vat_number"), type: "text" },
    affiliate_commission_pct: { label: t("admin.commission_pct"), type: "number", suffix: "%" },
    affiliate_commission_pct_2: { label: t("admin.commission_pct") + " Tier 2", type: "number", suffix: "%" },
    affiliate_commission_pct_3: { label: t("admin.commission_pct") + " Tier 3", type: "number", suffix: "%" },
    affiliate_recurring: { label: "Affiliate Recurring", type: "boolean" },
    affiliate_recurring_months: { label: "Recurring " + t("admin.months_label"), type: "number" },
    affiliate_payout_threshold: { label: t("admin.paid_out") + " Threshold", type: "number", suffix: "EUR" },
    invoice_forward_email: { label: "BCC E-mail", type: "email" },
  };
  const [settings, setSettings] = useState([]);
  const [toggles, setToggles] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminProfile, setAdminProfile] = useState({ name: "", company_name: "" });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/admin/settings"),
      api.get("/admin/feature-toggles"),
      api.get("/admin/vat-rates"),
    ]).then(([s, ft, vr]) => {
      setSettings(s.data);
      setToggles(ft.data);
      setVatRates(vr.data);
      setAdminProfile({ name: user?.name || "", company_name: user?.company_name || "" });
    }).catch(console.error).finally(() => setLoading(false));
  }, [user]);

  const updateSetting = async (id, value) => {
    try {
      await api.put(`/admin/settings/${id}`, { value });
      toast.success(t("admin.setting_updated"));
      setSettings((prev) => prev.map((s) => s.id === id ? { ...s, value } : s));
    } catch { toast.error("Fout"); }
  };

  const toggleFeature = async (id, enabled) => {
    try {
      await api.put(`/admin/feature-toggles/${id}`, { enabled });
      setToggles((prev) => prev.map((t) => t.id === id ? { ...t, enabled } : t));
      toast.success(t("admin.toggle_updated"));
    } catch { toast.error("Fout"); }
  };

  const updateVatRate = async (code, rate, reverseCharge) => {
    try {
      const vat = vatRates.find(v => v.country_code === code);
      await api.put(`/admin/vat-rates/${code}`, {
        country_name: vat?.country_name || "", vat_rate: parseFloat(rate),
        reverse_charge: reverseCharge,
      });
      setVatRates(prev => prev.map(v => v.country_code === code ? {...v, vat_rate: parseFloat(rate), reverse_charge: reverseCharge} : v));
      toast.success(`BTW ${code} bijgewerkt`);
    } catch { toast.error("Fout"); }
  };

  const saveAdminProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put("/auth/profile", adminProfile);
      toast.success(t("admin.profile_saved"));
    } catch { toast.error("Fout bij opslaan"); }
    finally { setSavingProfile(false); }
  };

  const renderSettingInput = (s) => {
    const config = SETTING_LABELS[s.key] || { label: s.key?.replace(/_/g, " "), desc: "", type: "text" };

    if (config.type === "language") {
      return (
        <Select defaultValue={s.value} onValueChange={(v) => updateSetting(s.id, v)}>
          <SelectTrigger className="w-48 h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="nl">Nederlands (nl)</SelectItem>
            <SelectItem value="en">English (en)</SelectItem>
            <SelectItem value="fr">Français (fr)</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    if (config.type === "boolean") {
      return (
        <Switch
          checked={s.value === "true"}
          onCheckedChange={(v) => updateSetting(s.id, v ? "true" : "false")}
        />
      );
    }

    return (
      <div className="flex items-center gap-2">
        <Input
          defaultValue={s.value}
          type={config.type === "number" ? "number" : config.type === "email" ? "email" : "text"}
          className="w-48 h-8 text-sm"
          onBlur={(e) => { if (e.target.value !== s.value) updateSetting(s.id, e.target.value); }}
        />
        {config.suffix && <span className="text-xs text-muted-foreground">{config.suffix}</span>}
      </div>
    );
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <h1 className="text-2xl font-bold">{t("admin.settings")}</h1>

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general"><Settings className="w-3.5 h-3.5 mr-1.5" />{t("admin.general")}</TabsTrigger>
          <TabsTrigger value="toggles"><ToggleRight className="w-3.5 h-3.5 mr-1.5" />{t("admin.feature_toggles")}</TabsTrigger>
          <TabsTrigger value="vat"><Globe className="w-3.5 h-3.5 mr-1.5" />{t("admin.vat_per_country")}</TabsTrigger>
          <TabsTrigger value="profile"><User className="w-3.5 h-3.5 mr-1.5" />{t("admin.admin_profile")}</TabsTrigger>
        </TabsList>

        {/* General Settings - improved */}
        <TabsContent value="general" className="space-y-3 mt-4">
          {settings.map((s) => {
            const config = SETTING_LABELS[s.key] || { label: s.key?.replace(/_/g, " "), desc: "" };
            return (
              <Card key={s.id} className="bg-card border-border" data-testid={`setting-${s.key}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex-1 min-w-0 mr-4">
                    <p className="font-medium text-sm">{config.label}</p>
                    {config.desc && <p className="text-xs text-muted-foreground mt-0.5">{config.desc}</p>}
                  </div>
                  {renderSettingInput(s)}
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* Feature Toggles */}
        <TabsContent value="toggles" className="space-y-3 mt-4">
          {toggles.map((ft) => (
            <Card key={ft.id} className="bg-card border-border" data-testid={`toggle-${ft.key}`}>
              <CardContent className="p-4 flex items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm capitalize">{ft.key?.replace(/_/g, " ")}</p>
                    <Badge variant={ft.enabled ? "default" : "secondary"} className={`text-xs ${ft.enabled ? "bg-emerald-500/20 text-emerald-400 border-0" : ""}`}>
                      {ft.enabled ? "Actief" : "Inactief"}
                    </Badge>
                  </div>
                  {ft.description && (
                    <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed flex items-start gap-1.5">
                      <Info className="w-3 h-3 shrink-0 mt-0.5" />{ft.description}
                    </p>
                  )}
                </div>
                <Switch checked={ft.enabled} onCheckedChange={(v) => toggleFeature(ft.id, v)} data-testid={`toggle-switch-${ft.key}`} />
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* VAT Rates */}
        <TabsContent value="vat" className="mt-4">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t("admin.vat_rates_title")}</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">{t("admin.vat_rates_desc")}</p>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("admin.country")}</TableHead>
                    <TableHead>{t("admin.code")}</TableHead>
                    <TableHead className="text-right">{t("admin.vat_pct")}</TableHead>
                    <TableHead className="text-center">{t("admin.vat_reversed")}</TableHead>
                    <TableHead className="text-right">{t("admin.result")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vatRates.map((vr) => (
                    <TableRow key={vr.country_code} data-testid={`vat-${vr.country_code}`}>
                      <TableCell className="font-medium">{vr.country_name}</TableCell>
                      <TableCell className="font-mono-data text-sm">{vr.country_code}</TableCell>
                      <TableCell className="text-right">
                        <Input type="number" step="0.1" defaultValue={vr.vat_rate} className="w-20 h-7 text-sm font-mono-data inline-block text-right"
                          onBlur={(e) => { if (parseFloat(e.target.value) !== vr.vat_rate) updateVatRate(vr.country_code, e.target.value, vr.reverse_charge); }}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox checked={vr.reverse_charge}
                          onCheckedChange={(v) => updateVatRate(vr.country_code, vr.vat_rate, v)}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        {vr.reverse_charge ? (
                          <Badge variant="outline" className="text-xs text-amber-500 border-amber-500/30">{t("admin.reversed_label")}</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">{vr.vat_rate}%</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Admin Profile */}
        <TabsContent value="profile" className="mt-4">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2"><User className="w-4 h-4" />{t("admin.admin_profile")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-accent/30 rounded-lg p-4">
                <p className="text-sm"><strong>E-mail:</strong> <span className="font-mono-data">{user?.email}</span></p>
                <p className="text-sm mt-1"><strong>Rol:</strong> <Badge variant="destructive" className="ml-1">{user?.role}</Badge></p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t("admin.name")}</Label><Input value={adminProfile.name} onChange={(e) => setAdminProfile({...adminProfile, name: e.target.value})} className="mt-1" /></div>
                <div><Label>{t("admin.company_name")}</Label><Input value={adminProfile.company_name} onChange={(e) => setAdminProfile({...adminProfile, company_name: e.target.value})} className="mt-1" /></div>
              </div>
              <Button onClick={saveAdminProfile} disabled={savingProfile} className="bg-[#2CA8AC] hover:bg-[#248F92]">
                <Save className="w-4 h-4 mr-2" />{savingProfile ? "..." : t("admin.save_profile")}
              </Button>
              <div className="border-t border-border pt-4 mt-4">
                <h4 className="font-semibold text-sm flex items-center gap-2 mb-2"><KeyRound className="w-4 h-4" />{t("admin.change_password")}</h4>
                <p className="text-xs text-muted-foreground mb-3">{t("admin.change_password_desc")}</p>
                <Button variant="outline" onClick={async () => {
                  try {
                    await api.post("/auth/request-reset", { email: user?.email, frontend_url: window.location.origin });
                    toast.success("Reset e-mail verzonden naar " + user?.email);
                  } catch { toast.error("Fout bij verzenden"); }
                }}>
                  <Mail className="w-4 h-4 mr-2" />{t("admin.password_reset_btn")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
